import React, {useEffect, useState} from 'react';
import { styled } from '@mui/material/styles';
import {useDispatch, useSelector} from "react-redux";
import { useTranslation } from 'react-i18next';

import {appSetAlert, appSetModalContent, appSetUUID} from "../../../redux/app/appActions";
import Construct from "../Construct";

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import axios from "axios";
import {
	PAGE_LAYOUT_CONFIGURATION,
	PAGE_SAVE_DATA_FULFILLED,
	PAGE_SAVE_DATA_REJECTED
} from "../../../redux/page/pageTypes";
import {axiosDelay} from "../../../config/config";
import useDataSource from "../../../hooks/useDataSource";
import {getSecurityAccess} from "../../../utilities/SecurityUtilities";
import {getFieldFormatters} from "../../../utilities/DataSourceUtilities";
import {formatValue} from "../../../utilities/FormatUtilities";
import {push} from "connected-react-router";
import {LinearProgress} from "@mui/material";
import {uuid} from "../../../utilities/GeneralUtilities";
import {dataReloadSources} from "../../../redux/data/dataActions";


const PREFIX = 'SmartButton';

const classes = {
	root: `${PREFIX}-root`,
	linearProgress: `${PREFIX}-linearProgress`,
	container: `${PREFIX}-container`
};

const StyledBox = styled(Box)((
	{
		theme
	}
) => ({
	[`&.${classes.root}`]: {
		height: '100%',
		background: 'transparent'
	},

	[`& .${classes.linearProgress}`]: {
		height: '1px'
	},

	[`& .${classes.container}`]: {
		height: '100%',
		margin: '0',
		display: 'flex'
	}
}));

export default function SmartButton({id, configuration, onLoading, onLoaded}) {
	const dispatch = useDispatch();
	const { t } = useTranslation();

	// App State
	const authEmail = useSelector(state => state.auth.email);
	const projectUserGroups = useSelector(state => state.user.projectUserGroups);
	const appUUID = useSelector(state => state.app.uuid);
	const config = useSelector(state => state.config);

	// Component State
	const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);
	const [buttonConfig, setButtonConfig] = useState(null);
	const [link, setLink] = useState(null);
	const [isDisabled, setIsDisabled] = useState(true);

	//     configuration: {
	//          dataSource: {
	//              name: <datasource>
	//              fields: {
	//                  value: <value field name from datasource>
	//              }
	//          },
	//          element: {
	//              button: {
	//                  color: inherit | primary | secondary | success | error | info | warning | string
	//                  label: <button label>
	//                  size: large | medium | small
	//                  variant: contained | outlined | text
	//              }
	//              buttonLink: {
	//                  type: modal | externalPost | externalGet | smartLinkFromDS (using a DataSource) | smartLink | staticLink
	//
	//					>> externalPost | externalGet | smartLink
	//					config: {
	// 						dataSource: {
	// 							name: <datasource>
	// 						},
	// 						paramNames: [
	// 							<fieldname1>,
	// 							<fieldname2>
	// 						],
	// 						paramValues: [
	// 							{
	// 								type: <datasource> (uses one of the two dataSources and the below field),
	// 								source: name of one of the two dataSources two use,
	// 								field: <fieldname>
	// 							},
	// 							{
	// 								type: "text" (adds a hard code value),
	// 								source: null,
	// 								value: <hardcoded value to use>
	// 							},
	// 							{
	// 								type: "user" (uses the users signedin email address),
	// 								source: null,
	// 								value: null
	// 							}
	// 						],
	//						endpointPath: <url>,
	//						// messages not applicable to smartLink
	// 						messages: {
	//							success: <success message for app alert>,
	//							error: <error message for app alert>
	// 						}
	//					}
	//
	//					>> modal
	// 					config: {
	//						title: <Title on Modal>,
	//						parent: {}
	//						paper:	{}
	//						component: {}
	// 					}
	//
	//					>> smartLinkFromDS (with or without a datasource to build link) | staticLink
	// 					config: {
	//	        	        newWindow: true | false
	//  	                type: file | ftp | mailto | tel | internal | http
	//      	            path: url path
	// 					}
	//              }
	//              buttonPosition: {
	//                  alignItems: stretch | center | flex-start | flex-end | baseline | initial | inherit
	//                  justifyContent: flex-start | flex-end | center | space-between | space-around | initial | inherit
	//              }
	//          }
	//			security: {
	//				"restrict": [2]
	//			}
	//      }

	const dataSourceName = configuration.dataSource?.name ? configuration.dataSource?.name : '';
	const valueField = configuration?.dataSource?.fields?.value ? configuration?.dataSource?.fields?.value : '';
	const dataSourceExternalPost = configuration?.element?.buttonLink?.config?.dataSource?.name ? configuration?.element?.buttonLink?.config?.dataSource?.name : '';
	const dataSource = useDataSource(dataSourceName);
	const dataSourcePost = useDataSource(dataSourceExternalPost);

	// -- ELEMENT: Button
	const buttonColor = configuration?.element?.button?.color ? configuration?.element?.button?.color : 'primary';
	let buttonLabel = configuration?.element?.button?.label ? configuration?.element?.button?.label : 'Button';
	const buttonSize = configuration?.element?.button?.size ? configuration?.element?.button?.size : 'large';
	const buttonVariant = configuration?.element?.button?.variant ? configuration?.element?.button?.variant : 'contained';

	const buttonType = configuration?.element?.buttonLink?.type.toLowerCase();
	const buttonLinkTarget = configuration?.element?.buttonLink?.newWindow === true ? '_blank' : '';

	const setUUID = configuration?.element?.setUUID !== undefined ? configuration.element.setUUID : false;

	// -- ELEMENT: Button Position
	const buttonPositionAlignItems = configuration?.element?.buttonPosition?.alignItems ? configuration?.element?.buttonPosition?.alignItems : 'center';
	const buttonPositionJustifyContent = configuration?.element?.buttonPosition?.justifyContent ? configuration?.element?.buttonPosition?.justifyContent : 'center';


	useEffect(() => {
		if (buttonType === 'modal') {
			buttonLabel = configuration?.element?.button?.label;
			setButtonConfig(configuration.element.buttonLink.config);
			setIsDisabled(false);
		} else if (buttonType === 'externalpost') {
			buttonLabel = configuration?.element?.button?.label;
			setButtonConfig(configuration.element.buttonLink.config);
		} else if (buttonType === 'externalget') {
			buttonLabel = configuration?.element?.button?.label;
			setButtonConfig(configuration.element.buttonLink.config);
		} else if (buttonType === 'smartlinkfromds') {
			buttonLabel = configuration?.element?.button?.label;
			setButtonConfig(configuration.element.buttonLink.config);
		} else if (buttonType === 'smartlink') {
			buttonLabel = configuration?.element?.button?.label;
			setButtonConfig(configuration.element.buttonLink.config);
		} else if (buttonType === 'staticlink') {
			buttonLabel = configuration?.element?.button?.label;
			setButtonConfig(configuration.element.buttonLink.config);
			setIsDisabled(false);
		}
	}, []);

	// Loading
	useEffect(() => {
		if (dataSource.fetching === true) {
			if (isFunction(onLoading)) {
				onLoading();
			}
		}
	}, [dataSource.fetching]);

	// Loaded
	useEffect(() => {
		if (dataSource.fetching === false && dataSource.fetched === true) {
			if (isFunction(onLoaded)) {
				onLoaded();
			}
			setShowProgress(false);
		}
	}, [dataSource.fetched]);

	// Check DataSource is filtered to ONE record
	useEffect(() => {
		if (buttonType !== 'modal' && buttonType !== 'staticlink' && buttonType !== 'smartlink' && dataSourcePost === undefined) {
			// console.log('-----------------Static Button---------STATUS->');
			// console.log('id: {0}'.format(id));
			// console.log('fetching: {0}'.format(dataSource.fetching));
			// console.log('fetched: {0}'.format(dataSource.fetched));
			// console.log('error: {0}'.format(dataSource.error));
			// console.log('data:', dataSource.data);

			if (dataSource.error) {
				setIsDisabled(true);
			} else if (dataSource.data && dataSource.fetched) {
				if (dataSource.data.length > 1) {
					setIsDisabled(true);
				} else if (dataSource.data.length === 1) {
					const projectID = dataSource.data[0]?.projectid !== undefined ? dataSource.data[0].projectid : undefined;
					const restricted = getSecurityAccess(projectID, projectUserGroups, configuration.security.restrict);

					if (restricted) {
						setIsDisabled(true);
					} else {

						if (buttonType === 'smartlinkfromds') {
							// Get formatters from datasource
							const linkFormatters = getFieldFormatters(dataSource, valueField);
							setLink(formatValue(dataSource.data[0][valueField], linkFormatters, null, false));
						}

						setIsDisabled(false);
					}
				} else {
					setIsDisabled(true);
				}
			}
		}
	}, [dataSource.data, dataSource.fetching, dataSource.fetched, dataSource.error]);

	// Check BOTH DataSource and ExternalPost DataSource is filtered to ONE record
	useEffect(() => {
		if (buttonType !== 'modal' && buttonType !== 'smartlinkfromds' && buttonType !== 'staticlink' && dataSourcePost !== undefined) {
			// console.log('-----------------Static Button---------STATUS->');
			// console.log('id: {0}'.format(id));
			// console.log('fetching dataSource: {0}'.format(dataSource.fetching));
			// console.log('fetched dataSource: {0}'.format(dataSource.fetched));
			// console.log('error dataSource: {0}'.format(dataSource.error));
			// console.log('data dataSource:', dataSource.data);
			// console.log('fetching dataSourcePost: {0}'.format(dataSourcePost.fetching));
			// console.log('fetched dataSourcePost: {0}'.format(dataSourcePost.fetched));
			// console.log('error dataSourcePost: {0}'.format(dataSourcePost.error));
			// console.log('data dataSourcePost:', dataSourcePost.data);

			if (dataSource.error || dataSourcePost.error) {
				setIsDisabled(true);
			} else if ((dataSource.data && dataSource.fetched) && (dataSourcePost.data && dataSourcePost.fetched)) {
				if ((dataSource.data.length > 1) || (dataSourcePost.data.length > 1)) {
					setIsDisabled(true);
				} else if ((dataSource.data.length === 1) && (dataSourcePost.data.length === 1)) {
					const restricted = getSecurityAccess(dataSource.data[0].projectid, projectUserGroups, configuration.security.restrict);
					const restrictedAgain = getSecurityAccess(dataSourcePost.data[0].projectid, projectUserGroups, configuration.security.restrict);

					if (restricted && restrictedAgain) {
						setIsDisabled(true);
					} else {

						if (buttonConfig) {

							let disableMe = false;
							if (buttonType === 'smartlink') {
								const paramNames = buttonConfig.paramNames;
								const paramValues = buttonConfig.paramValues;
								let fullParamString = "";
								if (buttonConfig.endpoint.internalLoader === true) {
									fullParamString = config.data.dataLoader.apiDataLoader + buttonConfig.endpoint.path + "?";
								} else if (buttonConfig.endpoint.internalLoader === false) {
									fullParamString = buttonConfig.endpoint.path + "?";
								}
								for (let i = 0; i < paramNames.length; i++) {
									const paramName = paramNames[i];
									const paramValue = getParamValue(paramValues[i]);
									if (paramValue === null) {
										// Break and keep button disabled if a param does not have a value
										disableMe = true;
										break;
									}
									const paramString = paramName + "=" + encodeURIComponent(paramValue);

									fullParamString = fullParamString + (i === 0 ? "" : "&") + paramString;
								}
								setLink(fullParamString);
							}

							setIsDisabled(disableMe ? true : false);
						} else {
							// console.log('No Button Config!!!!');
							setIsDisabled(true);
						}
					}
				} else {
					setIsDisabled(true);
				}
			}
		}
	}, [buttonConfig, dataSource.data, dataSource.fetching, dataSource.fetched, dataSource.error, dataSourcePost.data, dataSourcePost.fetching, dataSourcePost.fetched, dataSourcePost.error]);

	const clickHandler = () => {

		if (setUUID) {
			dispatch(appSetUUID(null));
		}

		if (buttonType === 'modal') {

			const modelConfig = {
				title: buttonConfig.title,
				parent: buttonConfig.parent,
				paper: buttonConfig.paper,
				button: null,
				content: <Construct layout={{configuration: buttonConfig.component}} />,
				tooltipContent: ""
			}

			dispatch(appSetModalContent(modelConfig));
		} else if (buttonType === 'externalpost') {
			sendExternalPost(buttonConfig);
		} else if (buttonType === 'externalget') {
			sendExternalGet(buttonConfig);
		} else if (buttonType === 'smartlinkfromds') {
			sendSmartLinkFromDS();
		} else if (buttonType === 'smartlink') {
			sendSmartLink();
		} else if (buttonType === 'staticlink') {
			sendStaticLink();
		}

	}

	const sendExternalPost = (postConfig) => {

		setShowProgress(true);
		setIsDisabled(true);

		setTimeout(function () {
			const fieldNames = postConfig.paramNames;
			const fieldValues = postConfig.paramValues;
			let fullURLPath = "";
			if (postConfig.endpoint.internalLoader === true) {
				fullURLPath = config.data.dataLoader.apiDataLoader + postConfig.endpoint.path;
			} else if (postConfig.endpoint.internalLoader === false) {
				fullURLPath = postConfig.endpoint.path;
			}
			let postBody = {};
			for (let i = 0; i < fieldNames.length; i++ ) {
				const paramName = fieldNames[i];
				const paramValue = getParamValue(fieldValues[i]);

				postBody[paramName] = paramValue;
			}

			// if a file to download and needs to return a zip, get axiosFileConfig ready
			let zip = false;
			if (postConfig?.zip !== undefined) {
				zip = postConfig.zip;
			}

			const axiosFileConfig = {
				responseType: zip ? 'arraybuffer' : ''
			};

			// Auth: Remove SV parameter, External Logic App has oAuth policy created to use Bearer token with Issuer and Audience
			// https://docs.microsoft.com/en-us/azure/logic-apps/logic-apps-securing-a-logic-app?tabs=azure-portal#generate-shared-access-signatures-sas
			axios.post(fullURLPath, postBody, axiosFileConfig)
				.then(function (response) {
					// console.log('**SUCCESS**', response);

					// if a file to download, process it
					const contentDisposition = response.headers['content-disposition'];
					if (contentDisposition !== undefined) {
						if (contentDisposition.indexOf("attachment;") > -1) {
							const url = window.URL.createObjectURL(new Blob([response.data]));
							const link = document.createElement('a');
							link.href = url;
							link.setAttribute('download', contentDisposition.substring(contentDisposition.indexOf("attachment; filename=") + 21, contentDisposition.length));
							document.body.appendChild(link);
							link.click();
						}
					}

					setShowProgress(false);
					setIsDisabled(false);
					dispatch(appSetAlert(null, postConfig.messages.success, 'success', 10000));
					dispatch(dataReloadSources(postConfig.reloadDataSources));

					// If setUUID is enabled
					if (setUUID) {
						const newGuid = uuid();
						dispatch(appSetUUID(newGuid));
					}

					// If Redirect is enabled
					// if (postConfig?.redirect !== undefined) {
					// 	if (postConfig.redirect.enabled) {
					// 		dispatch(push('{0}'.format(postConfig.redirect.path)));
					// 	}
					// }

				})
				.catch(function (error) {
					console.log('**ERROR**', error);
					setShowProgress(false);
					setIsDisabled(false);
					dispatch(appSetAlert('Error!', postConfig.messages.error, 'error', 10000));
					if (setUUID) {
						const newGuid = uuid();
						dispatch(appSetUUID(newGuid));
					}
				});
		}, axiosDelay);

	}

	const sendExternalGet = (getConfig) => {

		setShowProgress(true);
		setIsDisabled(true);

		setTimeout(function () {
			const paramNames = getConfig.paramNames;
			const paramValues = getConfig.paramValues;
			let fullParamString = "";
			if (getConfig.endpoint.internalLoader === true) {
				fullParamString = config.data.dataLoader.apiDataLoader + getConfig.endpoint.path + "?";
			} else if (getConfig.endpoint.internalLoader === false) {
				fullParamString = getConfig.endpoint.path + "?";
			}
			for (let i = 0; i < paramNames.length; i++ ) {
				const paramName = paramNames[i];
				const paramValue = getParamValue(paramValues[i]);
				const paramString = paramName + "=" + encodeURIComponent(paramValue);

				fullParamString = fullParamString + (i === 0 ? "" : "&") + paramString;
			}

			axios.get(fullParamString)
				.then(function (response) {
					// console.log('**SUCCESS**', response);
					setShowProgress(false);
					setIsDisabled(false);
					dispatch(appSetAlert(null, getConfig.messages.success, 'success', 10000));
					if (setUUID) {
						const newGuid = uuid();
						dispatch(appSetUUID(newGuid));
					}
				})
				.catch(function (error) {
					console.log('**ERROR**', error);
					setShowProgress(false);
					setIsDisabled(false);
					dispatch(appSetAlert('Error!', getConfig.messages.error, 'error', 10000));
					if (setUUID) {
						const newGuid = uuid();
						dispatch(appSetUUID(newGuid));
					}
				});
		}, axiosDelay);

	}

	const getParamValue = (paramConfig) => {
		let value = null;

		if (paramConfig.type.toLowerCase() === 'datasource') {
			if (dataSource.name.toLowerCase() === paramConfig.source.toLowerCase()) {
				value = dataSource.data[0][paramConfig.field];
			} else if (dataSourcePost !== undefined) {
				if (dataSourcePost.name.toLowerCase() === paramConfig.source.toLowerCase()) {
					value = dataSourcePost.data[0][paramConfig.field]
				}
			}
		} else if (paramConfig.type.toLowerCase() === 'text') {
			if (paramConfig.value !== null) {
				// Check for replace $values needing to be set here
				if (paramConfig.value.indexOf("$uuid") > -1) {
					value = paramConfig.value.replace("$uuid", appUUID);
				} else if (paramConfig.value.indexOf("$sourceSite") > -1) {
					value = paramConfig.value.replace("$sourceSite", config.data.dataLoader.sourceSite);
				} else if (paramConfig.value.indexOf("$fileStorage") > -1) {
					value = paramConfig.value.replace("$fileStorage", config.data.dataLoader.fileStorage);
				} else if (paramConfig.value.indexOf("$db") > -1) {
					value = paramConfig.value.replace("$db", config.data.authentication.apiSource);
				} else {
					value = paramConfig.value;
				}
			} else {
				value = paramConfig.value !== null ? paramConfig.value : 'N/A';
			}
		} else if (paramConfig.type.toLowerCase() === 'mapdatasource') {
			const pathValues = [];
			const valueFields = paramConfig.directory.valueFields;

			for (let i = 0; i < valueFields.length; i++ ) {
				if (dataSource.name.toLowerCase() === paramConfig.source.toLowerCase()) {
					pathValues.push(dataSource.data[0][valueFields[i]]);
				} else if (dataSourcePost !== undefined) {
					if (dataSourcePost.name.toLowerCase() === paramConfig.source.toLowerCase()) {
						pathValues.push(dataSourcePost.data[0][valueFields[i]]);
					}
				}
			}
			value = {path: paramConfig.directory.path, valueFields: pathValues}
		} else if (paramConfig.type.toLowerCase() === 'textarray') {
			value = paramConfig.value;
		} else if (paramConfig.type.toLowerCase() === 'user') {
			value = authEmail;
		}

		return value;
	}

	const sendSmartLinkFromDS = () => {
		if (setUUID) {
			const newGuid = uuid();
			dispatch(appSetUUID(newGuid));
		}
		window.open(link.toString(), buttonLinkTarget);
	}

	const sendSmartLink = () => {
		if (setUUID) {
			const newGuid = uuid();
			dispatch(appSetUUID(newGuid));
		}
		window.open(link.toString(), buttonLinkTarget);
	}

	const sendStaticLink = () => {
		// If NOT using a datasource and just passing in a value
		const linkValueFormatter = [
			{
				type: "link2",
				config: {
					"type": buttonConfig.type
				}
			}
		]
		if (setUUID) {
			const newGuid = uuid();
			dispatch(appSetUUID(newGuid));
		}
		window.open(formatValue(buttonConfig.path, linkValueFormatter, null, false), buttonLinkTarget);
	}

	function isFunction(functionToCheck) {
		return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
	}

	return (
		<StyledBox className={classes.root}>
			{showProgress && dataSource.fetching &&
				<LinearProgress className={classes.linearProgress}/>
			}
			{showProgress && !dataSource.fetching &&
				<LinearProgress className={classes.linearProgress}></LinearProgress>
			}
			<Box className={classes.container} style={{alignItems: buttonPositionAlignItems, justifyContent: buttonPositionJustifyContent}}>
				<Button color={buttonColor} size={buttonSize} variant={buttonVariant} onClick={() =>{ clickHandler() }} disabled={isDisabled}>
					{t(buttonLabel)}
				</Button>
			</Box>
		</StyledBox>
	);
}
